import { clsx } from 'clsx';
import { motion } from 'motion/react';
import type { PropsWithChildren } from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useScrollProgress } from '../../hooks/useScrollProgress';
import { Layout } from '../Layout/Layout';
import * as styles from './ScrollGradient.css';

export interface ScrollGradientProps extends PropsWithChildren {
  readonly maxHeight?: number | string;
  readonly onScrollEnd?: (scrollEnd: boolean) => void;
  readonly className?: string;
}

export interface ScrollGradientRef {
  readonly scrollToBottom: () => void;
}

export const ScrollGradient = forwardRef<ScrollGradientRef, ScrollGradientProps>(
  ({ children, maxHeight = 300, onScrollEnd, className: externalClassName }, ref) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const isMaxHeightNumber = typeof maxHeight === 'number';

    useImperativeHandle(ref, () => ({
      scrollToBottom: () => {
        contentRef.current?.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' });
      },
    }));

    const { hasScroll, scrollYProgress } = useScrollProgress({
      container: contentRef,
      maxHeight: isMaxHeightNumber ? maxHeight : undefined,
      onScrollEnd,
    });

    return (
      <Layout
        as={motion.div}
        className={externalClassName}
        position="relative"
        style={{
          [styles.contentScrollProgressVarName]: scrollYProgress,
        }}
        width="100%"
      >
        <Layout
          className={clsx({
            [styles.gradientWrapper]: hasScroll,
          })}
          maxHeight={isMaxHeightNumber ? `${maxHeight}px` : maxHeight}
          overflowY="auto"
          ref={contentRef}
          role="presentation"
        >
          {children}
        </Layout>
      </Layout>
    );
  },
);
